import { cn } from "../../lib/utils";
import { getShortcutsList } from "../../hooks/useShortcuts";

interface ShortcutsHelpProps {
  open: boolean;
  onClose: () => void;
}

const CATEGORY_LABELS: Record<string, string> = {
  general: "General",
  navigation: "Navigation",
  views: "Views",
  actions: "Actions",
};

const CATEGORY_ORDER = ["general", "navigation", "views"];

export function ShortcutsHelp({ open, onClose }: ShortcutsHelpProps) {
  if (!open) return null;

  const shortcuts = getShortcutsList();

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 animate-fade-in"
        onClick={onClose}
        onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
        role="button"
        tabIndex={-1}
      />

      {/* Dialog */}
      <div className="absolute left-1/2 top-[15%] -translate-x-1/2 w-full max-w-[480px] animate-stagger-in">
        <div
          className={cn(
            "rounded-[var(--radius-lg)] border border-[var(--color-border-stroke)]",
            "bg-[var(--color-bg-surface)] shadow-[var(--shadow-modal)]",
            "overflow-hidden",
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-[var(--spacing-4)] py-[var(--spacing-3)] border-b border-[var(--color-border-stroke)]">
            <span className="text-[14px] font-semibold text-[var(--color-text-primary)]">Keyboard Shortcuts</span>
            <button
              onClick={onClose}
              className={cn(
                "text-[14px] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]",
                "transition-colors duration-[var(--duration-fast)] cursor-pointer",
              )}
            >
              <kbd className="text-[11px] bg-[var(--color-bg-hover)] rounded-[4px] px-[6px] py-[2px] font-mono">ESC</kbd>
            </button>
          </div>

          {/* Content */}
          <div className="p-[var(--spacing-4)] space-y-[var(--spacing-4)] max-h-[60vh] overflow-y-auto">
            {CATEGORY_ORDER.map((cat) => {
              const items = shortcuts.filter((s) => s.category === cat);
              if (items.length === 0) return null;
              return (
                <div key={cat}>
                  <h3 className="text-[11px] font-semibold text-[var(--color-text-muted)] uppercase tracking-[0.06em] mb-[var(--spacing-2)]">
                    {CATEGORY_LABELS[cat]}
                  </h3>
                  <div className="space-y-[var(--spacing-0_5)]">
                    {items.map((item) => (
                      <div
                        key={item.keys}
                        className="flex items-center justify-between py-[var(--spacing-1)] px-[var(--spacing-2)] rounded-[var(--radius-sm)]"
                      >
                        <span className="text-[14px] text-[var(--color-text-secondary)]">{item.label}</span>
                        <div className="flex items-center gap-[var(--spacing-1)]">
                          {item.keys.split(" ").map((k, i) => (
                            <kbd
                              key={i}
                              className={cn(
                                "inline-flex items-center justify-center min-w-[24px] h-[22px]",
                                "text-[11px] font-mono text-[var(--color-text-primary)]",
                                "bg-[var(--color-bg-hover)] border border-[var(--color-border-stroke)]",
                                "rounded-[4px] px-[6px]",
                              )}
                            >
                              {k}
                            </kbd>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
